import type { ApiRequestObject } from '../../lib/flotiq-client/types/api-request';
import type { ContentObjectReference } from '../../lib/flotiq-client/types/api-response';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import {
  archiveContentObject,
  batchArchiveContentObjects,
  batchPublishContentObjects,
  batchUnpublishContentObjects,
  getContentObjectReferences,
  publishContentObject,
  scheduleContentObject,
  unpublishContentObject,
  unscheduleContentObject,
} from '../../lib/flotiq-client';
import { parseResponseErrors } from '../../lib/flotiq-client/response-errors';
import { getTestProps } from '../../lib/helpers';
import { invalidateContentObjects } from '../../lib/queryClientHelpers';

import { defaultButtonsProps, useModals } from '../../contexts/ModalContext';

import { BoxWithArrowIcon, WarningTriangleRedIcon } from '../../images/shapes';

import useSelectedSpace from '../useSelectedSpace';
import useToken from '../useToken';
import CascadeModalContent from './CascadeModalContent/CascadeModalContent';

const CONCURRENCY_LIMIT = 5;
const CASCADE_BATCH_THRESHOLD = 20;

const runWithConcurrency = async <TItem, TResult>(
  items: TItem[],
  callback: (item: TItem) => Promise<TResult>,
  shouldStop?: () => boolean,
) => {
  let itemIndex = 0;

  const runWorker = async (): Promise<void> => {
    while (itemIndex < items.length && !shouldStop?.()) {
      const currentIndex = itemIndex++;
      await callback(items[currentIndex]);
    }
  };

  return Promise.all(
    Array.from(
      { length: Math.min(CONCURRENCY_LIMIT, items.length) },
      runWorker,
    ),
  );
};

const useObjectStatusChange = (
  canCascadePublish = false,
  onSuccess?: (successIds: string[]) => void,
  testId?: string,
) => {
  const modal = useModals();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const jwt = useToken();
  const { space } = useSelectedSpace();

  const showConfirmationModal = (action: string, count: number) =>
    modal({
      title: (
        <div className="inline-flex text-red font-bold text-3xl items-center">
          <WarningTriangleRedIcon className="h-5 mr-2.5" />
          {t('Global.Warning')}
        </div>
      ),
      content:
        action === 'archive'
          ? t('ObjectStatus.Confirmation.Archive.Content', {
              count: count,
            })
          : t('ObjectStatus.Confirmation.Unpublish.Content', {
              count: count,
            }),
      buttons: [
        {
          key: 'archive',
          label:
            action === 'archive'
              ? t('ObjectStatus.Confirmation.Archive.Confirm')
              : t('ObjectStatus.Confirmation.Unpublish.Confirm'),
          result: true,
          color: 'red',
          iconImage: <BoxWithArrowIcon className="w-3 min-w-3" />,
          iconPosition: 'start',
        },
        {
          ...defaultButtonsProps.cancel,
          label: t('Global.Cancel'),
          result: false,
        },
      ],
    });

  const showCascadeConfirmationModal = (
    objectsCount: number,
    references: ContentObjectReference[],
    referencesError?: boolean,
    skippedCheck?: boolean,
  ) => {
    let contentText: string;
    if (skippedCheck) {
      contentText = t(`ObjectStatus.CascadePublish.SkippedCheck`);
    } else if (referencesError) {
      contentText = t(`ObjectStatus.CascadePublish.ReferencesError`, {
        count: objectsCount,
      });
    } else {
      contentText = t(`ObjectStatus.CascadePublish.Content`, {
        count: objectsCount,
      });
    }

    return modal({
      title: (
        <div
          className={twMerge(
            'font-bold text-3xl',
            objectsCount > 1 && 'text-center',
          )}
        >
          {t(`ObjectStatus.CascadePublish.Title`)}
        </div>
      ),
      content: (
        <>
          <p
            className={twMerge(
              'mb-5 text-lg',
              (referencesError || skippedCheck || objectsCount > 1) &&
                'text-center',
            )}
          >
            {contentText}
          </p>
          {objectsCount === 1 && (
            <CascadeModalContent
              references={references}
              {...getTestProps(testId, 'cascade', 'testId')}
            />
          )}
        </>
      ),
      size: 'lg',
      contentAdditionalClasses: twMerge(
        'pt-0',
        (referencesError || skippedCheck || objectsCount > 1) && 'pb-14',
      ),
      buttons: [
        {
          key: 'cascade',
          color: 'blue',
          label: t(`ObjectStatus.CascadePublish.Cascade`, {
            count: objectsCount,
          }),
          result: true,
        },
        {
          key: 'single',
          color: 'blueBordered',
          label: t(`ObjectStatus.CascadePublish.Single`, {
            count: objectsCount,
          }),
          result: false,
        },
      ],
    });
  };

  const checkSingleObjectReferences = async (
    objectId: string,
    contentTypeName: string,
  ) => {
    const {
      body: objectReferences,
      status: objectReferencesStatus,
      ok,
    } = await getContentObjectReferences(jwt, space, {
      objectType: contentTypeName,
      objectId,
    });

    if (!ok) {
      throw parseResponseErrors(objectReferences, objectReferencesStatus);
    }

    const refs = objectReferences.filter(
      ({ status, draftPublic }: ContentObjectReference) =>
        draftPublic && status !== 'public',
    );

    return refs;
  };

  const isCascadePublish = async (objects: Record<string, string[]>) => {
    const count = Object.values(objects).flatMap((ids) => ids).length;

    // skip preflight for large batches, show modal immediately
    if (count > CASCADE_BATCH_THRESHOLD) {
      return showCascadeConfirmationModal(count, [], false, true);
    }

    const cascadeReferences: ContentObjectReference[] = [];
    let referencesError = false;
    let foundEarlyExit = false;

    await runWithConcurrency(
      Object.entries(objects).flatMap(([contentTypeName, ids]) =>
        ids.map((objectId) => ({ contentTypeName, objectId })),
      ),
      async ({ contentTypeName, objectId }) => {
        try {
          const refs = await checkSingleObjectReferences(
            objectId,
            contentTypeName,
          );
          if (refs.length > 0) {
            cascadeReferences.push(...refs);
            foundEarlyExit = true;
          }
        } catch {
          referencesError = true;
          foundEarlyExit = true;
        }
      },
      () => foundEarlyExit,
    );

    if (!cascadeReferences.length && !referencesError) {
      return false;
    }

    return showCascadeConfirmationModal(
      count,
      cascadeReferences,
      referencesError,
    );
  };

  const updateMultipleStatuses = async (
    contentTypeName: string,
    ids: string[],
    action: string,
  ): Promise<{ success: string[]; error: string[] }> => {
    const method = {
      unpublish: batchUnpublishContentObjects,
      archive: batchArchiveContentObjects,
      publish: batchPublishContentObjects,
    };

    try {
      if (!(action in method)) {
        throw new Error('Invalid action for batch update');
      }
      const apiMethod = method[action as keyof typeof method];
      const { body, status, ok } = await apiMethod(jwt, space, {
        objectType: contentTypeName,
        ...ids,
      });

      if (!ok) {
        throw parseResponseErrors(body, status);
      }

      return { success: ids, error: [] };
    } catch {
      return { success: [], error: ids };
    }
  };

  const updateSingleStatus = async (
    contentTypeName: string,
    id: string,
    action: string,
    values?: ApiRequestObject,
  ): Promise<{ success: string[]; error: string[] }> => {
    const method = {
      unpublish: unpublishContentObject,
      archive: archiveContentObject,
      publish: publishContentObject,
      schedule: scheduleContentObject,
      unschedule: unscheduleContentObject,
    };

    try {
      if (!(action in method)) {
        throw new Error('Invalid action for single update');
      }
      const apiMethod = method[action as keyof typeof method];
      const { body, status, ok } = await apiMethod(jwt, space, {
        objectType: contentTypeName,
        objectId: id,
        ...(values || {}),
      });

      if (!ok) {
        throw parseResponseErrors(body, status);
      }

      return { success: [id], error: [] };
    } catch {
      return { success: [], error: [id] };
    }
  };

  const buildUpdateTasks = ({
    objects,
    action,
    values,
    cascadePublish,
  }: {
    objects: Record<string, string[]>;
    action: string;
    values?: ApiRequestObject;
    cascadePublish: boolean;
  }): Array<() => Promise<{ success: string[]; error: string[] }>> => {
    const updateTasks: Array<
      () => Promise<{ success: string[]; error: string[] }>
    > = [];

    for (const [contentTypeName, ids] of Object.entries(objects)) {
      if (cascadePublish || ids.length === 1) {
        for (const id of ids) {
          updateTasks.push(() =>
            updateSingleStatus(contentTypeName, id, action, {
              ...values,
              ...(cascadePublish ? { hydrate: 1 } : {}),
            }),
          );
        }
        continue;
      }

      updateTasks.push(() =>
        updateMultipleStatuses(contentTypeName, ids, action),
      );
    }

    return updateTasks;
  };

  const showUpdateStatusesToast = (
    successIds: string[],
    errorIds: string[],
  ) => {
    if (errorIds.length > 0) {
      if (successIds.length > 0) {
        toast.success(
          t('ObjectStatus.Toast.PartialSuccess', {
            successIds: successIds.join(', '),
            errorIds: errorIds.join(', '),
          }),
        );
      } else {
        toast.error(t('ObjectStatus.Toast.Error', { count: errorIds.length }));
      }
      return;
    }

    toast.success(
      t('ObjectStatus.Toast.Success', { count: successIds.length }),
    );
  };

  const updateStatuses = async ({
    objects,
    action,
    values,
  }: {
    action: string;
    objects?: Record<string, string[]>;
    values?: ApiRequestObject;
  }) => {
    if (!objects) return { isCascade: false, contentTypes: [] };

    const allCount = Object.values(objects).flatMap((ids) => ids).length;
    const allContentTypes = Object.keys(objects);

    if (action === 'unpublish' || action === 'archive') {
      const result = await showConfirmationModal(action, allCount);
      if (!result) return { isCascade: false, contentTypes: [] };
    }

    const cascadePublish =
      action === 'publish' && canCascadePublish
        ? await isCascadePublish(objects)
        : false;

    if (typeof cascadePublish !== 'boolean')
      return { isCascade: false, contentTypes: [] };

    const successIds: string[] = [];
    const errorIds: string[] = [];
    const updateTasks = buildUpdateTasks({
      objects,
      action,
      values,
      cascadePublish,
    });

    await runWithConcurrency(updateTasks, async (runTask) => {
      const result = await runTask();
      successIds.push(...result.success);
      errorIds.push(...result.error);
    });

    if (successIds.length && onSuccess) {
      onSuccess(successIds);
    }

    showUpdateStatusesToast(successIds, errorIds);

    return { isCascade: cascadePublish, contentTypes: allContentTypes };
  };

  const afterMutate = (
    params: { isCascade: boolean; contentTypes: string[] } | undefined,
  ) => {
    const { isCascade, contentTypes } = params || {
      isCascade: false,
      contentTypes: [],
    };
    if (!contentTypes.length) return;
    return isCascade || contentTypes.length > 1
      ? invalidateContentObjects(queryClient)
      : invalidateContentObjects(queryClient, contentTypes[0]);
  };

  const { isPending, mutate } = useMutation({
    mutationFn: updateStatuses,
    onSettled: afterMutate,
  });

  return {
    updateStatuses: mutate,
    isPending,
  };
};

export default useObjectStatusChange;
